// src/pages/Dashboard.tsx
import {useState, useEffect, useMemo} from 'react';
import {
    Filter,
    X,
    ChevronDown,
    ChevronUp, Loader2
} from 'lucide-react';
import {AppraiserInfo, DashboardOrder, DashboardSearchRequest} from '../../../api/ApiTypes.ts';
import {useAppraiserApi, useDashApi} from "../../../context/ApiContext.tsx";
import Modal from "../../../components/Modal.tsx";
import { OrderSearch } from "../../../components/OrderSearch.tsx";
import {useNavigate} from "react-router-dom";
import {Pagination} from "../../../components/Pagination.tsx";
import {DashboardMobileView} from "./DashboardMobileView.tsx";
import {DashboardTableView} from "./DashboardTableView.tsx";
import {DashboardSkeletonTable} from "./DashboardSkeletonTable.tsx";
import {UpdatePaymentNavigator} from "../../../components/UpdatePaymentNavigator.tsx";


export const AppraiserDashboard = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<DashboardOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [totalItems, setTotalItems] = useState(0);
    const dashApi = useDashApi();
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 25,
    });
    const [totalPages, setTotalPages] = useState(0);
    const [expandedCardIds, setExpandedCardIds] = useState<number[]>([]);

    // Sorting state
    const [sorting, setSorting] = useState({
        field: '',
        direction: ''
    });

    // Search state
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [searchParams, setSearchParams] = useState<DashboardSearchRequest>({
        page: 1,
        limit: pagination.pageSize,
        sortField: sorting.field,
        sortDirection: sorting.direction
    });


    const [ isNavigateToPaymentsModalOpen, setIsNavigateToPaymentsModalOpen ] = useState<boolean>(false);
    const appraiserApi = useAppraiserApi();

    useEffect(() => {

        appraiserApi.getMyAppraiserInfo().then((appraiserInfo: AppraiserInfo) => {
            setIsNavigateToPaymentsModalOpen(
                !appraiserInfo.eppPartyData && appraiserInfo.readyForEppOnboarding
            )
        })
    }, []);

    const activeFiltersCount = useMemo(() => {
        const { usaFileNumber, borrowerName, loanNumber, propertyStreet, propertyCity, propertyState, propertyZip, createDateFrom, createDateTo, statusRefIds } = searchParams;
        return [
            usaFileNumber,
            borrowerName,
            loanNumber,
            propertyStreet,
            propertyCity,
            propertyState,
            propertyZip,
            createDateFrom,
            createDateTo,
            statusRefIds === undefined || statusRefIds.length > 0 ? 'hasStatus' : null
        ].filter(Boolean).length;
    }, [searchParams]);


    const fetchData = async () => {
        setLoading(true);
        setError('');

        try {
            // Update page and limit from current pagination state
            const currentParams: DashboardSearchRequest = {
                ...searchParams,
                page: pagination.pageIndex + 1,
                limit: pagination.pageSize
            };

            const response = await dashApi.getDash(currentParams);

            setData(response.items);
            setTotalItems(response.totalItems);
            setTotalPages(response.totalPages);
        } catch (err: any) {
            setError(err.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    // Update searchParams when sorting changes
    useEffect(() => {
        setSearchParams(prev => ({
            ...prev,
            sortField: sorting.field,
            sortDirection: sorting.direction
        }));
    }, [sorting]);

    useEffect(() => {
        fetchData();
    }, [pagination.pageIndex, pagination.pageSize, searchParams]);

    const handleSearch = (newSearchParams: DashboardSearchRequest) => {
        // Reset to first page when applying new search
        setPagination({
            ...pagination,
            pageIndex: 0
        });

        // Preserve sorting if not specified in search params
        const updatedParams = {
            ...newSearchParams,
            sortField: newSearchParams.sortField !== undefined ? newSearchParams.sortField : sorting.field,
            sortDirection: newSearchParams.sortDirection !== undefined ? newSearchParams.sortDirection : sorting.direction
        };

        // Update search parameters state
        setSearchParams(updatedParams);

        // Close the search modal
        setIsSearchModalOpen(false);
    };

    const handleClearSearch = () => {
        setSearchParams({
            page: 1,
            limit: pagination.pageSize,
            sortField: sorting.field,
            sortDirection: sorting.direction,
            statusRefIds: []
        });

        // Don't clear sorting when clearing filters
    };

    const handleViewOrder = (id: number) => {
        navigate(`/appraiser/orderdetails/${id}`)
        // Navigation logic here
    };

    const toggleCardExpansion = (id: number) => {
        setExpandedCardIds(prev =>
            prev.includes(id)
                ? prev.filter(cardId => cardId !== id)
                : [...prev, id]
        );
    };

    const getDueDateClass = (dueDate: string | null) => {
        if (!dueDate) return '';
        return new Date(dueDate) < new Date()
            ? 'text-red-600 bg-red-50'
            : 'text-gray-600 bg-gray-50';
    };

    // Handle sorting when a column header is clicked
    const handleSort = (field: string) => {
        setSorting(prev => {
            if (prev.field === field) {
                const direction = prev.direction === 'asc' ? 'desc' : prev.direction === 'desc' ? '' : 'asc';
                return { field: direction ? field : '', direction };
            }
            return { field, direction: 'asc' };
        });
    };


    // Get sort indicator icon
    const getSortIcon = (field: string) => {
        if (sorting.field !== field) return null;

        if (sorting.direction === 'asc') {
            return <ChevronUp className="h-4 w-4 inline ml-1" />;
        } else if (sorting.direction === 'desc') {
            return <ChevronDown className="h-4 w-4 inline ml-1" />;
        }

        return null;
    };

    return (
        <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6">
            {/* Header section */}
            <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4">
                <h1 className="text-2xl font-bold text-slate-800">Orders Dashboard</h1>

                <div className="flex gap-2 w-full sm:w-auto">
                    {activeFiltersCount > 0 && (
                        <button
                            onClick={handleClearSearch}
                            className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 flex items-center gap-1 sm:gap-2 hover:cursor-pointer"
                        >
                            <X className="h-4 w-4" />
                            <span>Clear ({activeFiltersCount})</span>
                        </button>
                    )}

                    <button
                        onClick={() => setIsSearchModalOpen(true)}
                        className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1 sm:gap-2 hover:cursor-pointer"
                    >
                        <Filter className="h-4 w-4" />
                        <span>Filter</span>
                    </button>
                </div>
            </div>

            {/* Error display */}
            {error && (
                <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
                    {error}
                </div>
            )}

            {/* Desktop Skeleton Loader */}
            {loading && <DashboardSkeletonTable />}

            {/*Mobile Spinning Loader */}
            {loading && (
                <div className="md:hidden flex items-center justify-center p-8">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-600">Loading orders...</span>
                </div>
            )}

            {/* Empty state */}
            {!loading && data.length === 0 && (
                <div className="text-center py-12 px-4">
                    <div className="text-gray-400 mb-2">
                        <Filter className="h-12 w-12 mx-auto" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">No orders found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Try adjusting your search filters to find what you're looking for.
                    </p>
                </div>
            )}

            {/* Desktop Table View (hidden on mobile) */}
            {!loading && data.length > 0 && (
                <DashboardTableView
                    data={data}
                    onSort={handleSort}
                    sorting={sorting}
                    onView={handleViewOrder}
                    getSortIcon={getSortIcon}
                    getDueDateClass={getDueDateClass}
                />
            )}

            {!loading && data.length > 0 && (
                <DashboardMobileView
                    data={data}
                    expandedCardIds={expandedCardIds}
                    onToggleExpand={toggleCardExpansion}
                    onView={handleViewOrder}
                    getDueDateClass={getDueDateClass}
                />
            )}

            {/* Sorting indicator for mobile */}
            {!loading && data.length > 0 && sorting.field && (
                <div className="md:hidden mb-2 flex items-center text-sm text-gray-600">
                    <span>Sorted by {sorting.field} ({sorting.direction === 'asc' ? 'ascending' : 'descending'})</span>
                    <button
                        onClick={() => setSorting({ field: '', direction: '' })}
                        className="ml-2 text-blue-600 hover:underline"
                    >
                        Clear
                    </button>
                </div>
            )}

            {/* Pagination controls */}
            {!loading && data.length > 0 && (
                <Pagination
                    pageIndex={pagination.pageIndex}
                    pageSize={pagination.pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={(pageIndex) =>
                        setPagination((prev) => ({ ...prev, pageIndex }))
                    }
                    onPageSizeChange={(pageSize) =>
                        setPagination({ pageIndex: 0, pageSize }) // reset to page 0 on size change
                    }
                />
            )}

            {/* Search Modal */}
            <Modal
                isOpen={isSearchModalOpen}
                onClose={() => setIsSearchModalOpen(false)}
                title="Filter Orders"
                maxWidth="2xl"
            >
                <OrderSearch
                    initialValues={searchParams}
                    onSearch={handleSearch}
                    onCancel={() => setIsSearchModalOpen(false)}
                    isSubmitting={loading}
                />
            </Modal>

            <Modal
                isOpen={isNavigateToPaymentsModalOpen}
                onClose={()=> setIsNavigateToPaymentsModalOpen(false)}
                title="Please Update Payment Information"
                maxWidth="2xl"
            >
                <UpdatePaymentNavigator
                    onCancel={()=> setIsNavigateToPaymentsModalOpen(false)}
                />
            </Modal>
        </div>
    );
};